import * as React from 'react'

import {
  ISiteLocation,
  ITypeFieldFilterOptions,
  LocationType,
} from '~/client/graph'
import { parseDefaultOptions } from '~/client/src/shared/components/ResourcePicker/parseDefaultOptions'
import ResourcePicker from '~/client/src/shared/components/ResourcePicker/ResourcePicker'
import { withKeepSelected } from '~/client/src/shared/components/ResourcePicker/resourceWorkflowVisibility'
import { useResourcePickerCards } from '~/client/src/shared/components/ResourcePicker/useResourceRenderItem'
import type { ICreationFieldOptions } from '~/client/src/shared/components/ResourceViewEdit/useResourceFields'
import { IWarningNote } from '~/client/src/shared/components/WarningNote/WarningNote'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import BsonObjectId from '~/client/src/shared/utils/BsonObjectId'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import SitePermitCreationFormStore from '../../../../../WorkflowCreationForm.store'

const hasId = (value: unknown): value is ISiteLocation =>
  value != null && typeof value === 'object' && 'id' in value

interface IProps {
  store: SitePermitCreationFormStore
  resourceTypeId: string
  /**
   * Null for a record the legacy kind stores do not hold. A value is displayed through
   * them, so such a record is hidden unless already selected.
   */
  getLocationType: (id: string) => LocationType | null
  records: IResourceRecord[]
  loading: boolean
  projectId?: string
  defaultName?: string
  startCreatingRef?: React.MutableRefObject<(() => void) | null>
  onClose?: () => void
  /** Override the auto-detected selected IDs (e.g. for field types that
   *  store values in a shape without a plain `.id` property). */
  initialSelectedIds?: string[]
  isItemDisabled?: (recordId: string) => boolean
  getNote?: (recordId: string) => IWarningNote | null
  recordPredicate?: (record: IResourceRecord) => boolean
  resourceSchema: ResourceSchema
  countLabel?: string
  creationRootDefaults?: Record<string, any>
  creationFieldOptions?: ICreationFieldOptions
  mode?: 'single' | 'multi'
  /** Replaces the field's filterOptions. */
  filterOptions?: ITypeFieldFilterOptions[]
  isCreationAllowed?: boolean
}

const FormResourcePicker: React.FC<IProps> = ({
  store,
  resourceTypeId,
  getLocationType,
  records,
  loading,
  projectId,
  defaultName = 'New Resource',
  startCreatingRef,
  onClose,
  initialSelectedIds: initialSelectedIdsProp,
  isItemDisabled,
  getNote,
  recordPredicate,
  resourceSchema,
  countLabel,
  creationRootDefaults,
  creationFieldOptions,
  mode: modeProp,
  filterOptions,
  isCreationAllowed = true,
}) => {
  const { properties, propertiesReady, ready, renderItem } =
    useResourcePickerCards(resourceTypeId, resourceSchema)

  const field = store.selectedField
  const workflowFieldsMap = store.editablePermit?.mainFieldsByIdMap
  const allowCreateNew = isCreationAllowed && (field?.allowCreateNew ?? true)
  const mode = modeProp ?? (field?.isMultiple ? 'multi' : 'single')
  const allowSelectExisting = field?.allowSelectExisting ?? true
  const initialCreating =
    store.fieldSelectionMode === 'createNew' ||
    (allowCreateNew && !allowSelectExisting)
  // A grouping the field leaves unset falls back to the resource's picker default.
  const defaults = React.useMemo(
    () => parseDefaultOptions(field?.defaultOptions),
    [field?.defaultOptions],
  )

  const selectedIds = React.useMemo(() => {
    if (initialSelectedIdsProp) return initialSelectedIdsProp
    return store.selectedFieldValues
      .flat()
      .filter(hasId)
      .map(v => String(v.id))
  }, [initialSelectedIdsProp, store.selectedFieldValues])

  const handleSelect = React.useCallback(
    (ids: string[]) => {
      // A value that stays selected keeps what was set on it (levels, precise location).
      const toLocation = (
        id: string,
        current?: ISiteLocation,
      ): ISiteLocation => ({
        levels: null,
        ...current,
        id,
        type: getLocationType(id) ?? current?.type ?? null,
      })
      const values = store.selectedFieldValues

      if (mode === 'multi') {
        const currentById = new Map(
          values
            .flat()
            .filter(hasId)
            .map(v => [String(v.id), v]),
        )
        // Kept values keep their order, new picks follow.
        const picked = new Set(ids)
        const orderedIds = [
          ...[...currentById.keys()].filter(id => picked.has(id)),
          ...ids.filter(id => !currentById.has(id)),
        ]
        store.changeSelectedFieldValue(
          orderedIds.map(id => toLocation(id, currentById.get(id))),
        )
      } else if (ids[0]) {
        // Only the edited slot, so a pick never takes another slot's levels.
        const current = values[store.selectedFieldIndex ?? 0]
        store.changeSelectedFieldValue(
          toLocation(
            ids[0],
            hasId(current) && String(current.id) === ids[0]
              ? current
              : undefined,
          ),
        )
      }
      store.updateSearchValue(EMPTY_STRING)
    },
    [store, mode, getLocationType],
  )

  const effectiveRecordPredicate = React.useMemo(
    () =>
      withKeepSelected(
        record =>
          getLocationType(record.id) !== null &&
          (!recordPredicate || recordPredicate(record)),
        selectedIds,
      ),
    [getLocationType, recordPredicate, selectedIds],
  )

  const handleCreateResource = React.useCallback(
    async (formData: Record<string, unknown>): Promise<string | null> => {
      const tempId = BsonObjectId.generate()
      const name = (formData.name as string) || defaultName
      store.addPendingResource(tempId, formData, name, resourceTypeId)
      return tempId
    },
    [store, defaultName, resourceTypeId],
  )

  return (
    <ResourcePicker
      records={records}
      resourceTypeId={resourceTypeId}
      mode={mode}
      selectedIds={selectedIds}
      onSelect={handleSelect}
      onCreateResource={allowCreateNew ? handleCreateResource : undefined}
      loading={loading || !ready}
      defaults={defaults}
      properties={properties}
      propertiesReady={propertiesReady}
      resourceSchema={resourceSchema}
      filterOptions={filterOptions ?? field?.filterOptions}
      workflowFieldsMap={workflowFieldsMap}
      initialCreating={initialCreating}
      allowSelectExisting={allowSelectExisting}
      projectId={projectId}
      onClose={onClose}
      startCreatingRef={allowCreateNew ? startCreatingRef : undefined}
      isItemDisabled={isItemDisabled}
      getNote={getNote}
      recordPredicate={effectiveRecordPredicate}
      creationRootDefaults={creationRootDefaults}
      creationFieldOptions={creationFieldOptions}
    >
      <ResourcePicker.Toolbar />
      <ResourcePicker.List renderItem={renderItem} countLabel={countLabel} />
      <ResourcePicker.EmptyState />
      <ResourcePicker.Footer onCancel={onClose} />
    </ResourcePicker>
  )
}

export default FormResourcePicker
